export function registerChunkLoadAutoReload(): void {
  // Webpack dynamic imports (code-splitting) can fail during/after a deploy when an old tab
  // requests a chunk that no longer exists on the server/CDN. A single reload resolves this
  // by fetching the latest index.html + main bundle.
  // sessionStorage persists for the lifetime of the current tab session, so this prevents
  // repeated reload attempts (reload-loop) in that same session.
  const RELOAD_FLAG = "metx:chunk-reload";
  const WINDOW_FLAG = "__metx_chunk_reload";

  const shouldHandle = (message: string, filename = ""): boolean => {
    const normalizedMessage = message.toLowerCase();
    const normalizedFilename = filename.toLowerCase();

    if (
      normalizedMessage.includes("chunkloaderror") ||
      normalizedMessage.includes("loading chunk") ||
      normalizedMessage.includes("failed to fetch dynamically imported module") ||
      normalizedMessage.includes("importing a module script failed")
    ) {
      return true;
    }

    // "Unexpected token '<'" usually means HTML was returned where JS was expected.
    // In deploy races this often happens when an old chunk URL gets an HTML fallback/error page.
    if (normalizedMessage.includes("unexpected token '<'")) {
      return (
        normalizedFilename.endsWith(".js") ||
        normalizedFilename.includes("/static/js/") ||
        normalizedMessage.includes(".chunk.js") ||
        normalizedMessage.includes("/static/js/")
      );
    }

    return false;
  };

  const triggerReloadOnce = (): void => {
    let alreadyReloaded = false;

    try {
      alreadyReloaded = !!window.sessionStorage.getItem(RELOAD_FLAG);
    } catch {
      alreadyReloaded = !!(window as any)[WINDOW_FLAG];
    }

    if (alreadyReloaded) {
      return;
    }

    try {
      window.sessionStorage.setItem(RELOAD_FLAG, "1");
    } catch {
      // ignore
    }

    (window as any)[WINDOW_FLAG] = true;

    // Small backoff gives edge/CDN invalidation a moment before hard reload.
    // Without this, immediate reload can occasionally hit the same stale edge response again.
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  window.addEventListener("error", (event) => {
    const errorEvent = event as ErrorEvent;
    const message = String(errorEvent?.message || "");
    const filename = String(errorEvent?.filename || "");
    if (shouldHandle(message, filename)) {
      triggerReloadOnce();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason: any = (event as PromiseRejectionEvent)?.reason;
    const message = String(reason?.message || reason || "");
    const stack = String(reason?.stack || "");
    if (shouldHandle(`${message}\n${stack}`)) {
      triggerReloadOnce();
    }
  });
}
